import React from "react";
import { StyleSheet, useTokens } from "./theme";
import { Icon } from "./icons";
import { Banner, Chip, Segmented } from "./ui";
import { FiltersProvider, rangeLabel, useFilters } from "./filters";
import { personLabel, useView, workspaceLabel } from "./data";
import DateRangePicker from "./DateRangePicker";
import WorkspacePicker from "./WorkspacePicker";
import Overview from "./views/Overview";
import Capabilities from "./views/Capabilities";
import Models from "./views/Models";
import Quality from "./views/Quality";
import Demand from "./views/Demand";
import Outcomes from "./views/Outcomes";
import Latency from "./views/Latency";
import Cost from "./views/Cost";
import Versions from "./views/Versions";
import Workspaces from "./views/Workspaces";
import People from "./views/People";
import Conversations from "./views/Conversations";
import ConversationDrawer from "./views/ConversationDrawer";

/**
 * GTM agent analytics — adoption, outcomes, capabilities, quality, latency,
 * cost, releases, workspaces, people and every conversation.
 *
 * Data comes from the agent's own read API (`/obs/v1/*` on hp-mcp-server)
 * through `/api/agent-analytics`, which adds the admin key and audit-logs every
 * conversation opened. Every filter is in the URL (`filters.js`), so a click on
 * one tab fills the filter the others read.
 */

/** Tabs in groups: the summary, how well it works, who uses it. */
const TABS = [
  [
    { id: "overview", label: "Overview", icon: "overview", View: Overview },
  ],
  [
    { id: "capabilities", label: "Capabilities", icon: "capabilities", View: Capabilities },
    { id: "models", label: "Agent models", icon: "sparkle", View: Models },
    { id: "quality", label: "Quality", icon: "quality", View: Quality },
    { id: "demand", label: "Demand", icon: "demand", View: Demand },
    { id: "outcomes", label: "Retention & value", icon: "retention", View: Outcomes },
    { id: "latency", label: "Speed & errors", icon: "speed", View: Latency },
    { id: "cost", label: "AI cost", icon: "dollar", View: Cost },
    { id: "versions", label: "Releases", icon: "versions", View: Versions },
  ],
  [
    { id: "accounts", label: "Workspaces", icon: "workspaces", View: Workspaces },
    { id: "users", label: "People", icon: "people", View: People },
    { id: "conversations", label: "Conversations", icon: "conversations", View: Conversations },
  ],
];
const ALL_TABS = TABS.flat();

/** Views whose API reads a person filter; elsewhere the chip says it is not applied. */
const PERSON_AWARE = new Set(["users", "conversations"]);

const STATUS_WORDS = { finished: "Finished", open: "Still open", stopped: "Stopped by user", failed: "Failed" };

export default function AgentAnalytics() {
  return (
    <FiltersProvider>
      <Page />
    </FiltersProvider>
  );
}

function Page() {
  const { vars } = useTokens();
  const { f, set, params, refresh } = useFilters();
  const tab = ALL_TABS.find((t) => t.id === f.tab) ?? ALL_TABS[0];
  // The workspace list for the picker ignores the workspace filter itself.
  const { data: accounts } = useView("accounts", { ...params, accountId: undefined });
  const { data: overview } = useView("overview", params);

  return (
    <div className="aa-root" style={vars}>
      <StyleSheet />
      <header className="aa-header">
        <div className="aa-container">
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 0 10px", flexWrap: "wrap" }}>
            <div className="aa-icon-tile" style={{ width: 36, height: 36, borderRadius: 10, background: "var(--aa-accent)", color: "#fff" }}>
              <Icon name="sparkle" size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>GTM Agent Analytics</div>
              <div style={{ fontSize: 12, color: "var(--aa-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                <span className="aa-dot" style={{ color: overview?.signals?.recording === false ? "var(--aa-warn)" : "var(--aa-good)" }} />
                {overview?.agent?.url ? (
                  <span>
                    {f.env === "staging" ? "Staging" : "Production"} · <span className="aa-mono">{overview.agent.url.replace(/^https?:\/\//, "")}</span>
                  </span>
                ) : (
                  <span>Connecting to the agent…</span>
                )}
              </div>
            </div>
            <Segmented label="Environment" value={f.env} onChange={(v) => set({ env: v })} options={[["production", "Production"], ["staging", "Staging"]]} />
            <WorkspacePicker accounts={accounts?.accounts} />
            <DateRangePicker />
            <button type="button" className="aa-btn aa-btn-icon" onClick={refresh} title="Refresh" aria-label="Refresh">
              <Icon name="refresh" size={15} />
            </button>
            {overview?.mastraTracesUrl && (
              <a className="aa-btn" href={overview.mastraTracesUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                Traces
                <Icon name="external" size={13} />
              </a>
            )}
          </div>
          <nav className="aa-tabs" role="tablist">
            {TABS.map((group, gi) => (
              <React.Fragment key={gi}>
                {gi > 0 && <span className="aa-tab-sep" />}
                {group.map((t) => (
                  <button key={t.id} type="button" role="tab" className="aa-tab" aria-selected={tab.id === t.id} onClick={() => set({ tab: t.id })}>
                    <Icon name={t.icon} size={15} />
                    {t.label}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </header>

      <main className="aa-container aa-content">
        <ActiveFilters tabId={tab.id} />
        {overview?.signals && !overview.signals.recording && (
          <Banner title="This agent is not recording Mastra signals">
            Tracing is off on it (in local development it needs <code>HP_TRACING=on</code>). Conversations, requests, people and ratings are complete; the
            funnel, quality, friction, credits, records saved, cost and latency read from Mastra and stay empty until it records.
          </Banner>
        )}
        <tab.View key={tab.id} overview={overview} accounts={accounts} />
      </main>

      {f.session && <ConversationDrawer sessionId={f.session} onClose={() => set({ session: null })} />}
    </div>
  );
}

/** The filters in force, as removable chips — set here, from the header, or by a click anywhere on the page. */
function ActiveFilters({ tabId }) {
  const { f, set } = useFilters();
  const chips = [];
  if (f.range === "custom") chips.push({ k: "Date", v: rangeLabel(f), clear: { range: null, from: null, to: null } });
  if (f.ws) chips.push({ k: "Workspace", v: workspaceLabel(f.ws), clear: { ws: null } });
  if (f.person)
    chips.push({
      k: "Person",
      v: personLabel(f.person),
      clear: { person: null },
      title: PERSON_AWARE.has(tabId) ? undefined : "Applied on People and Conversations only",
      note: !PERSON_AWARE.has(tabId),
    });
  if (f.status) chips.push({ k: "Requests", v: STATUS_WORDS[f.status] ?? f.status, clear: { status: null }, note: tabId !== "conversations", title: tabId !== "conversations" ? "Applied on Conversations only" : undefined });
  if (!chips.length) return null;
  const clearAll = Object.assign({}, ...chips.map((c) => c.clear));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--aa-muted)", marginRight: 2 }}>
        <Icon name="filter" size={13} />
        Filtered by
      </span>
      {chips.map((c) => (
        <span key={c.k} style={{ opacity: c.note ? 0.6 : 1 }}>
          <Chip k={c.k} v={c.v} title={c.title} onRemove={() => set(c.clear)} />
        </span>
      ))}
      <button type="button" className="aa-link" style={{ fontSize: 12, marginLeft: 4 }} onClick={() => set(clearAll)}>
        Clear all
      </button>
    </div>
  );
}
